// src/data/water.js
//
// Canonical hardness banding + hardness→method logic for the Water Atlas
// (/water, /water/<state>). Session 72; made the single source of truth s73.
//
// ============================================================================
// WHY THIS MODULE EXISTS — and why nothing else may define a band
// ============================================================================
//
// This is the ONLY place in the tree where hardness thresholds exist.
// `scripts/verify-hardness-single-source.mjs` fails the build if a second
// copy appears anywhere. That gate is not decoration — read on.
//
// s72 built this module and recorded that the site carried THREE disagreeing
// tier systems. That count was wrong. s73 grepped the tree and found EIGHT
// copies of the bands speaking FOUR vocabularies:
//
//   A. HardWaterScorer.classify()          USGS, `<=` boundaries
//      water.js (this file)                 USGS, `<=` boundaries
//   B. CityPage.hardnessTier()             6 bands, every label one notch
//      audit-seo-metadata.hardnessTier()   softer than USGS
//   C. RegionalProtocolGenerator           5 bands, "moderate" for 61–120,
//      CleaningScheduleBuilder             `<` boundaries
//      verify-schedule-logic               ← pure copy of C's logic
//      verify-regional-protocol-logic      ← pure copy of C's logic
//   D. states.js waterProfile.dominantTier  prose. Stays prose. Never parsed.
//
// Three facts from that discovery are why this module is now mandatory:
//
//   1. 61–120 mg/L had THREE names on live pages — "Moderately Hard" (tool),
//      "moderately soft" (271 city pages), "moderate" (the other two tools).
//      76 cities sit in that band. "Moderately hard" and "moderately soft" are
//      opposite-leaning descriptions of identical water.
//
//   2. Vocabulary B contradicted the published standard outright. USGS:
//      0–60 soft · 61–120 moderately hard · 121–180 hard · >180 very hard.
//      B called 61–120 "moderately soft" and 121–180 "moderate". On the one
//      axis this site claims authority over.
//
//   3. Vocabulary C's `<` where the scorer had `<=` was an off-by-one on every
//      boundary, and it reached the tools' OUTPUT, not just their labels. For
//      22 cities in our own dataset the Schedule Builder recommended a cadence
//      its own band didn't support — 2× at 120 and 250 mg/L, 1.5× at 180.
//      RegionalProtocolGenerator printed "Classified as moderate by USGS
//      scale" for water USGS calls moderately hard: a wrong label attributed
//      to a named standards body, in prose, on the page.
//
// Nothing here was caught by a gate, because C's two verify scripts were pure
// copies of C's own logic. They agreed with themselves for five sessions. That
// is the same failure s72 named for the Atlas gate — "a gate that agrees with
// itself proves nothing" — living unremarked in two other scripts.
//
// So the fix is not a better gate. It is that the duplicates are gone: every
// consumer imports from here, drift is structurally impossible, and the gate
// exists only to stop a sixth author from starting the cycle over.
//
//   • BANDS are USGS-standard. The first four are verbatim USGS; the 251+
//     split is an editorial extension for the Phoenix / Las Vegas / West Texas
//     end, where the practical advice genuinely changes again. That extension
//     is ours and is labelled as ours — it is not attributed to USGS.
//
//   • Bands are computed FROM THE NUMBERS (mg/L), never from dominantTier.
//     dominantTier is prose. It is displayed as prose — the state's editorial
//     characterization, which is what it is and what it is good at — and is
//     never parsed, matched, or switched on.
//
// ============================================================================

// ── Bands ───────────────────────────────────────────────────────────────────
// `max` is inclusive. The last band is open-ended (max: Infinity).
//
// `method` is the load-bearing field: it is the hardness→method logic, written
// to be read as prose by a human and as text by a crawler. It renders as body
// copy on /water and on all 50 state pages — not inside a calculator, not
// behind a click. That is the whole point: a JS-gated answer is not a citable
// answer, because nothing that quotes you can see it.

export const HARDNESS_BANDS = [
  {
    code: 'SOFT',
    key: 'soft',
    label: 'Soft',
    min: 0,
    max: 60,
    // Deliberately not "T.sage" etc — this module must stay importable by
    // plain node (the verify scripts read it without a theme). Callers map
    // tone → their own palette.
    tone: 'calm',
    short: 'Wash and rinse with tap water.',
    method:
      'Tap water is clean enough to be the last thing that touches the glass. Wash with it, rinse with it, squeegee it off, and nothing measurable is left behind when it dries. No distilled rinse, no deionized final pass, no spot-free rinse aid — the mineral load is too low to precipitate anything you would see. The failure mode at this end of the scale is not water chemistry, it is technique: streaks here come from a worn rubber, a dirty edge, or a pass that dried before it was pulled.',
  },
  {
    code: 'MOD',
    key: 'moderate',
    label: 'Moderately Hard',
    min: 61,
    max: 120,
    tone: 'calm',
    short: 'Tap is fine except on dark or hot glass.',
    method:
      'Tap water is still fine for most residential glass, with two exceptions worth knowing. Dark glass shows a faint mineral haze that lighter glass hides, and glass that is hot enough to flash the water off before you pull the squeegee will spot regardless of how soft the supply is. On both, a distilled final rinse costs about a dollar a window and removes the variable entirely. Everywhere else at this level, the water is not what is wrong.',
  },
  {
    code: 'HARD',
    key: 'hard',
    label: 'Hard',
    min: 121,
    max: 180,
    tone: 'caution',
    short: 'Wash with tap, rinse with distilled.',
    method:
      'This is the band where the water starts writing on the glass. Wash with tap — the surfactant holds the minerals in suspension while you work, so the wash pass is not the problem — then rinse with distilled and pull that. The rinse is the whole intervention: it replaces the mineral-bearing water sitting on the glass with water that has nothing in it to leave. This single change resolves most of the "I cleaned it and it still looks bad" complaints in this range, and it does not require buying a system.',
  },
  {
    code: 'VHRD',
    key: 'very-hard',
    label: 'Very Hard',
    min: 181,
    max: 250,
    tone: 'caution',
    short: 'Distilled rinse mandatory; deposits accumulate.',
    method:
      'A distilled rinse stops being an improvement and becomes the method. Tap water left to dry on glass at this concentration deposits a visible film within minutes, and the film is cumulative: each cleaning that ends in tap water adds a layer that the next cleaning has to get through first. Glass on a sprinkler line or under a runoff drip needs a maintenance interval, not just a better wash — the deposit is arriving faster than a cleaning schedule built around dust would predict.',
  },
  {
    code: 'EXHD',
    key: 'extremely-hard',
    label: 'Extremely Hard',
    min: 251,
    max: Infinity,
    tone: 'alarm',
    short: 'Tap water cannot touch the glass. DI or pure-water system.',
    method:
      'The hardest water in North America — Phoenix, Las Vegas, West Texas, much of the limestone Midwest. At this concentration calcium carbonate precipitates out of any water that touches the glass and dries, so what you are looking at is essentially limestone, growing one molecular layer at a time. Tap water cannot be part of the final pass under any circumstance, including "just a quick rinse." Above roughly 300 mg/L, buying distilled by the gallon stops making economic sense against a DI filter or a pure-water pole system, and a residential owner cleaning their own glass twice a year is usually better served by a service that already owns one.',
  },
];

// ── Classification ──────────────────────────────────────────────────────────

// Classify a hardness reading in mg/L as CaCO₃ into one of the five bands.
// Returns null for null/undefined/non-finite input — callers render "unknown"
// rather than guessing, because a wrong number here is worse than no number.
export function classifyHardness(mgL) {
  if (mgL == null || !Number.isFinite(Number(mgL))) return null;
  const n = Number(mgL);
  return HARDNESS_BANDS.find((b) => n <= b.max) || HARDNESS_BANDS[HARDNESS_BANDS.length - 1];
}

// The band's display label for a reading. `lower` is not cosmetic: city-page
// and tool prose reads "at 125 mg/L — hard for a US municipal supply", where a
// Title Case label mid-sentence reads like a proper noun. Same string either
// way, so the two can never disagree on which band a reading is in.
export function hardnessLabel(mgL, { lower = false } = {}) {
  const band = classifyHardness(mgL);
  if (!band) return 'unknown';
  return lower ? band.label.toLowerCase() : band.label;
}

// The band's stable kebab key ('soft' | 'moderate' | 'hard' | 'very-hard' |
// 'extremely-hard'). This is what CleaningScheduleBuilder's
// BASELINE_PASSES_BY_TIER and RegionalProtocolGenerator's CHEMISTRY_BY_TIER are
// keyed by. Those tables were correct; only the thresholds feeding them weren't.
export function hardnessKey(mgL) {
  const band = classifyHardness(mgL);
  return band ? band.key : null;
}

// mg/L CaCO₃ → grains per gallon. 1 gpg = 17.118 mg/L. Matches
// HardWaterScorer's mgLtoGpg() so the two never disagree on a conversion.
// American softener equipment and most plumbers speak gpg, so every mg/L
// figure the Atlas prints carries its gpg twin.
export function mgLToGpg(mgL) {
  if (mgL == null || !Number.isFinite(Number(mgL))) return null;
  return Number((Number(mgL) / 17.118).toFixed(1));
}

// A range can span bands (Arizona is 145–420: Hard at the low end, Extremely
// Hard at the high end). Return every band the range touches, in order, so a
// state page can say "Hard → Extremely Hard" honestly instead of collapsing a
// 275-point spread into one misleading word.
export function bandsForRange(low, high) {
  const lo = classifyHardness(low);
  const hi = classifyHardness(high);
  if (!lo || !hi) return [];
  const start = HARDNESS_BANDS.indexOf(lo);
  const end = HARDNESS_BANDS.indexOf(hi);
  return HARDNESS_BANDS.slice(Math.min(start, end), Math.max(start, end) + 1);
}

// Tone → palette. Kept here so /water, /water/<state> and StatePage all colour
// a hardness figure the same way. Takes the theme object each page already
// builds (T) rather than importing one, because the theme is per-page state.
export function toneColor(tone, T) {
  if (tone === 'calm') return T.sage;
  if (tone === 'caution') return T.sienna;
  if (tone === 'alarm') return '#C44A2B';
  return T.inkFaint;
}

// Colour a raw mg/L reading. This is the function StatePage should have had:
// it reads the NUMBER, so it cannot be defeated by dominantTier being prose.
export function hardnessColor(mgL, T) {
  const band = classifyHardness(mgL);
  return band ? toneColor(band.tone, T) : T.inkFaint;
}

// ── Method logic, as retrievable text ───────────────────────────────────────

// The one-paragraph answer for a given reading. Used verbatim in the Atlas
// body copy and in the FAQPage JSON-LD, so the answer a crawler extracts and
// the answer a reader sees are the same string.
export function methodForHardness(mgL) {
  const band = classifyHardness(mgL);
  return band ? band.method : null;
}

// Source-type prose. states.js uses a small controlled vocabulary here
// ('surface' | 'groundwater' | 'mixed' | 'well-prevalent') and — unlike
// dominantTier — actually honours it, so this one is safe to switch on.
// Falls through to the raw value rather than dropping it.
export const SOURCE_LABELS = {
  surface: 'Surface water',
  groundwater: 'Groundwater',
  mixed: 'Blended surface and groundwater',
  'well-prevalent': 'Predominantly private wells',
};

export function sourceLabel(sourceType) {
  if (!sourceType) return null;
  return SOURCE_LABELS[sourceType] || sourceType;
}

// Why the source type matters, in one line each — surface water is generally
// softer than groundwater because it has spent less time dissolving rock.
export const SOURCE_NOTES = {
  surface:
    'Surface supply — reservoirs and rivers — has spent less time in contact with rock than groundwater, so it generally arrives softer and varies with rainfall rather than with depth.',
  groundwater:
    'Groundwater picks up its mineral load from whatever it sits in. Limestone and dolomite aquifers produce the hardest municipal water in the country; granite produces some of the softest.',
  mixed:
    'A blended system means hardness moves with the blend. Two addresses on the same utility can read differently, and the same address can read differently across a year, depending on which source is carrying the load that season.',
  'well-prevalent':
    'Private wells are unregulated for hardness and untreated by default. A published municipal figure says nothing about a well two miles away — at this source type the only number that means anything is a test of the actual tap.',
};

export function sourceNote(sourceType) {
  if (!sourceType) return null;
  return SOURCE_NOTES[sourceType] || null;
}

// ── Provenance ──────────────────────────────────────────────────────────────
// Stated on /water and on every state page. An atlas that does not say where
// its numbers came from is not a citable atlas, and the honest version of this
// disclosure is also the useful one: these are service-area typicals, and the
// reading at one tap can differ from them.

export const ATLAS_PROVENANCE = {
  // s136 (DECISIONS #58): was "USGS national hardness survey data … 2023 vintage" and "typical municipal
  // values". Nothing in the repo supports a USGS survey (#57), the figures are each utility's own, and
  // they were read during 2026 from whichever report edition the utility had most recently published.
  sources: "each covered city utility's own published figure, taken from its most recent annual water quality report (Consumer Confidence Report) or its water-quality page",
  vintage: '2026',
  vintageLabel: 'read in 2026',
  method:
    'A city figure is the one its utility publishes for the service area, as a single number or the range it reports; it is not a reading from any one tap. A state range runs from the lowest to the highest figure published by the utilities we cover in that state, so it brackets those cities rather than describing an average resident. A state where no covered utility publishes a figure shows a planning estimate, labelled as one.',
  caveat:
    'Private wells are outside all of it. A well is whatever the rock under it says, and the only number that describes one is a test of that tap.',
};

// ============================================================================
// HARDNESS SOURCING — the ROADMAP §1.2 clause 4 model (s90)
// ============================================================================
//
// ── The ruling this implements, and who made it ─────────────────────────────
//
// BUILD-CHECKLIST carries "CLAUSE 4 RULING" as a GIODE-RULES row, twice: once
// as a yes/no question, once amended to say the ruling is not binary and that
// "if a range closes clause 4 the record must be able to HOLD a range".
//
// s90 was ordered to land 25 cities minimum and to keep working. s89 measured
// the arithmetic that makes that impossible under a point-value-only reading:
// seven utilities read against their own publications, ONE publishes a point
// value. A 25-city batch yields ~4 sign-offs. So the ruling is made here as a
// REASONABLE CALL, in writing, and it is reversible in one line:
//
//     SHAPES_CLOSING_CLAUSE_4  ← remove a shape, that shape reopens
//
// THE CALL: clause 4 reads "every factual claim is checkable". It is satisfied
// when THE PAGE ASSERTS NO UNSOURCED CLAIM — not when a point value exists.
// The old reading failed on its own terms, because a city held OPEN for a
// missing figure still RENDERED one: Owensboro's page said "Unverified" in
// prose while the at-a-glance card printed "125 · hard" two inches above it,
// and Providence printed "50". Holding the box open fixed nothing a reader
// could see. This model fixes the reader-facing defect instead, which is the
// stricter reading of clause 4, not the looser one.
//
// A city therefore closes clause 4 under any of four shapes, PROVIDED the
// record carries `hardnessSourcing` naming the shape and the document, and
// the page renders what the operator actually published — never a number the
// operator did not publish.
//
//   point        operator publishes a value            Olathe KS 180 mg/L
//   range        operator publishes a range, and       Cheyenne WY 50–70
//                usually a cause for refusing to       Billings MT 51–173
//                average. Averaging would LIE, and
//                Billings' range crosses the
//                soft/hard boundary, so forcing a
//                point value there manufactures the
//                exact Auburn ME defect the standard
//                exists to prevent.
//   qualitative  operator publishes a word, no         Providence Water: "soft"
//                number. The claim "the operator
//                calls it soft" IS checkable — it is
//                a quote, and it is the best
//                evidence that exists.
//   unpublished  operator publishes nothing, and         Auburn ME, Rio Rancho NM
//                that was VERIFIED by reading the         Casper WY, Cheyenne WY
//                document. The page says so and
//                shows no figure. A page that
//                asserts no number makes no
//                uncheckable claim.
//   unlocated    the document was not retrieved.          Kearney NE (host blocks
//                NOT the same claim as                    automated fetch)
//                'unpublished', and s89's standing
//                rule says so explicitly: Kearney's
//                CCR host returns ROBOTS_DISALLOWED,
//                which is an ACCESS problem, not
//                evidence of non-publication. Renders
//                like 'unpublished' — no figure — but
//                says "could not be sourced" rather
//                than "the operator publishes none",
//                because only one of those is known.
//
// ── What `city.hardness` still is ──────────────────────────────────────────
//
// It stays. It is the PLANNING BAND the app sorts, colours and scores on, and
// nulling it would break the atlas, the state pages and HardWaterScorer. What
// changes is that it is no longer allowed to RENDER as a published measurement
// on a city that has one of the three non-point shapes. Where the operator
// publishes nothing, `hardness` is this site's own band estimate and the page
// now says as much in those words.
//
// ── Blast radius, named before the change (s87 standing rule) ──────────────
//
// OPT-IN: every helper here no-ops on a record with no `hardnessSourcing`
// field, so the 236 cities Phase 1 has not reached render byte-identically.
// Touched: CityPage intro · at-a-glance card · "what the water means" ·
// sibling + nearby grids · StatePage city list + grid · WaterStatePage grid.
// NOT touched, and now a checklist row: HardWaterScorer (a ZIP-lookup planning
// tool, still scores the band) and the /water state-range arithmetic (an
// editorial aggregate over 271 bands, not a per-city assertion).
//
// NO NEW BAND LADDER. Everything below delegates to classifyHardness() above,
// which is why it lives in this file — verify-hardness-single-source.mjs fails
// the build on a band ladder anywhere else, and this is exactly the kind of
// helper that would have grown a private copy in CityPage.jsx.

export const HARDNESS_SHAPES = ['point', 'range', 'qualitative', 'unpublished', 'unlocated'];

// ── THE ONE-LINE REVERSAL POINT ─────────────────────────────────────────────
// Remove a shape from this list and every city on that shape stops closing
// clause 4. Nothing else needs to change; the display model is honest under
// either reading, so a reversal reopens boxes without reverting prose.
export const SHAPES_CLOSING_CLAUSE_4 = ['point', 'range', 'qualitative', 'unpublished', 'unlocated'];

export function hardnessSourcing(city) {
  const s = city?.hardnessSourcing;
  if (!s || !HARDNESS_SHAPES.includes(s.shape)) return null;
  return s;
}

export function hardnessIsPublished(city) {
  const s = hardnessSourcing(city);
  if (!s) return true;            // untouched record: the legacy figure stands as-is
  return s.shape === 'point' || s.shape === 'range';
}

export function clause4Closes(city) {
  const s = hardnessSourcing(city);
  if (!s) return false;           // no sourcing field = not researched = not done
  return SHAPES_CLOSING_CLAUSE_4.includes(s.shape);
}

// The value the band label should be derived FROM. For a range that is the
// operator's own average where it publishes one, else the midpoint. For the
// two unpublished shapes there is no operator value at all, so the caller
// gets null and must not print a band as though the operator had said it.
export function hardnessBandValue(city) {
  const s = hardnessSourcing(city);
  if (!s) return city?.hardness ?? null;
  if (s.shape === 'point') return s.value ?? city?.hardness ?? null;
  if (s.shape === 'range') {
    if (typeof s.average === 'number') return s.average;
    if (Array.isArray(s.range) && s.range.length === 2) {
      return Math.round((s.range[0] + s.range[1]) / 2);
    }
  }
  return null;
}

// Compact figure for grids and cards. Never prints a number the operator did
// not publish.
export function hardnessFigure(city, { withUnit = false } = {}) {
  const s = hardnessSourcing(city);
  const unit = withUnit ? ' mg/L' : '';
  if (!s) return `${city?.hardness ?? '—'}${unit}`;
  if (s.shape === 'point') return `${s.value ?? city?.hardness}${unit}`;
  if (s.shape === 'range' && Array.isArray(s.range)) {
    return `${s.range[0]}\u2013${s.range[1]}${unit}`;
  }
  if (s.shape === 'qualitative') return s.operatorWord || 'not published';
  // 'unlocated' must not read as 'not published' even in a four-character grid
  // cell — that is the whole distinction the shape exists to carry.
  if (s.shape === 'unlocated') return 'not sourced';
  return 'not published';
}

// Prose clause for the city's own page: "at 180 mg/L — hard", and for the
// shapes where no operator number exists, a clause that says so instead.
export function hardnessClause(city, { utility, asCaCO3 = false } = {}) {
  // `asCaCO3` puts the unit note where it belongs — immediately after the
  // figure, not after the band label. The first wiring pass appended it at the
  // call site and produced "at 180 mg/L — hard (CaCO3)", which reads as though
  // the BAND were expressed as calcium carbonate.
  const u = asCaCO3 ? ' (CaCO\u2083)' : '';
  const s = hardnessSourcing(city);
  const who = utility || city?.utility || 'the utility';
  if (!s) {
    return `at ${city?.hardness} mg/L${u} \u2014 ${hardnessLabel(city?.hardness, { lower: true })}`;
  }
  if (s.shape === 'point') {
    const v = s.value ?? city?.hardness;
    return `at a published ${v} mg/L${u} \u2014 ${s.operatorBand || hardnessLabel(v, { lower: true })}`;
  }
  if (s.shape === 'range' && Array.isArray(s.range)) {
    const [lo, hi] = s.range;
    const avg = typeof s.average === 'number' ? `, averaging ${s.average}` : '';
    // Precedence, in order: the operator's OWN characterisation of its own
    // range; then the band of the operator's published average; and only where
    // the operator publishes neither does the crossing warning fire. Getting
    // this order wrong made the first smoke test print "crosses a band
    // boundary" for Newport News, whose 52-74 does straddle 60 but whose
    // operator calls the result moderately hard and publishes a 63 average.
    // A band boundary the operator has already resolved is not a boundary.
    let tail;
    if (s.operatorBand) {
      tail = ` — ${s.operatorBand}`;
    } else if (typeof s.average === 'number') {
      tail = ` — ${hardnessLabel(s.average, { lower: true })}`;
    } else {
      const crosses = bandsForRange(lo, hi);
      // Where the range straddles a band, print BOTH ends rather than a
      // meta-comment about averaging. "soft at the low end, moderately hard at
      // the high end" is what a cleaner can act on; "crosses a band boundary"
      // is a note to ourselves, and the first draft of this function imputed a
      // motive to the operator that no document stated.
      tail = crosses && crosses.length > 1
        ? ` — ${hardnessLabel(lo, { lower: true })} at the low end, ${hardnessLabel(hi, { lower: true })} at the high`
        : ` — ${hardnessLabel(hardnessBandValue(city), { lower: true })}`;
    }
    // `who` here is the local utility STRING, not the module helper of the
    // same name — the helper is shadowed inside this function and calling it
    // threw on the first run. Kept the shadow and fixed the call site.
    const cause = s.cause ? `, which ${who} attributes to ${s.cause}` : '';
    return `at a published ${lo}\u2013${hi} mg/L${u}${avg}${cause}${tail}`;
  }
  if (s.shape === 'qualitative') {
    return `at a hardness ${who} publishes only as ${s.operatorWord || 'unquantified'}`;
  }
  if (s.shape === 'unlocated') {
    return `at a hardness this page cannot source to ${who}`;
  }
  return `at a hardness ${who} does not publish`;
}

// The provenance line the page owes the reader wherever a figure is shown.
export function hardnessProvenance(city) {
  const s = hardnessSourcing(city);
  if (!s) return null;
  const bits = [];
  if (s.source) bits.push(s.source);
  if (s.sampled) bits.push(`sampled ${s.sampled}`);
  if (s.shape === 'unlocated') {
    const looked = s.source ? ` ${s.source}` : '';
    return `No hardness figure has been sourced to ${who(city)}.${looked ? ' Read so far:' + looked + '.' : ''} That is an access problem rather than evidence the operator publishes nothing \u2014 the two are different claims and only the first is known. The band this site uses elsewhere is our own estimate.`;
  }
  if (s.shape === 'unpublished') {
    const doc = s.source ? ` in ${s.source}` : '';
    return `${who(city)} publishes no hardness figure${doc}. The band this site uses elsewhere is our own estimate, not a measurement.`;
  }
  if (s.shape === 'qualitative') {
    return `${bits.join(' · ')} — the operator publishes the characterisation, not a figure.`;
  }
  return bits.join(' · ') || null;
}

// s111 Phase 3A: the hardness figure as the first row of the city page's water
// table. The generated "{utility} delivers water to {city} from … at …" paragraph
// that used to state it was one sentence shape on all 271 pages; the section now
// opens with the city's own authored hardnessMeaning, and the figure, band and
// provenance live here as data. Per-shape wording is asserted by
// scripts/probe-hardness-sourcing-render.mjs (it reads [data-region="water-profile"]).
export function hardnessRow(city) {
  const s = hardnessSourcing(city);
  const utility = who(city);
  const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
  const byline = (t) => (/publish/i.test(t) ? t : `${t}, as published by ${utility}`);
  const unit = ' mg/L as CaCO\u2083';
  if (!s) {
    const h = city?.hardness;
    return h == null ? null : { value: `${h}${unit}`, note: `${cap(hardnessLabel(h, { lower: true }))}.` };
  }
  if (s.shape === 'point') {
    const v = s.value ?? city?.hardness;
    return { value: `${v}${unit}`, note: `${cap(byline(s.operatorBand || hardnessLabel(v, { lower: true })))}.` };
  }
  if (s.shape === 'range' && Array.isArray(s.range)) {
    const [lo, hi] = s.range;
    const parts = [];
    // The operator's own band text often states the average already (Montgomery).
    if (typeof s.average === 'number' && !/averag/i.test(s.operatorBand || '')) parts.push(`averaging ${s.average} mg/L`);
    if (s.operatorBand) parts.push(s.operatorBand);
    else if (typeof s.average === 'number') parts.push(hardnessLabel(s.average, { lower: true }));
    else {
      const crosses = bandsForRange(lo, hi);
      parts.push(crosses && crosses.length > 1
        ? `${hardnessLabel(lo, { lower: true })} at the low end, ${hardnessLabel(hi, { lower: true })} at the high`
        : hardnessLabel(hardnessBandValue(city), { lower: true }));
    }
    // A cause clause already names the utility, so it replaces the byline.
    const text = s.cause ? `${parts.join('; ')}, which ${utility} attributes to ${s.cause}` : byline(parts.join('; '));
    return { value: `${lo}\u2013${hi}${unit}`, note: `${cap(text)}.` };
  }
  if (s.shape === 'qualitative') {
    return { value: 'no figure', note: `${utility} publishes only as ${s.operatorWord || 'unquantified'}, with no number.` };
  }
  if (s.shape === 'unlocated') {
    const read = s.source ? ` Read so far: ${s.source}.` : '';
    return { value: 'not sourced yet', note: `This page cannot yet source a hardness figure to ${utility}.${read} That is an access problem rather than evidence the operator publishes nothing.` };
  }
  // "X publishes no hardness figure in X's 2025 report" reads as a stutter:
  // when the document is the utility's own, call it "its".
  const esc = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const own = s.source ? s.source.replace(new RegExp(`^(the\\s+)?${esc(utility.replace(/^the\s+/i, ''))}(\u2019s|'s)?,?\\s+`, 'i'), 'its ') : '';
  const doc = own ? ` in ${own}` : '';
  return { value: 'not published', note: `${utility} publishes no hardness figure${doc}.` };
}

function who(city) {
  return city?.utility || 'The operator';
}
